/* eslint-disable @typescript-eslint/ban-ts-comment */
import type { FilterDefinitions } from '@bottlebooks/gatsby-theme-event/src/components/Filters/useIndex';
import { defineMessage } from '@lingui/macro';
import { graphql } from 'gatsby';
import * as ProductHelpers from '@bottlebooks/gatsby-plugin-california-pdf/src/templates/VieVinum/helpers/product';
import type { Vv_ProductFiltersFragment } from './@types/productFilters.generated';
import stands from '@bottlebooks/gatsby-plugin-california-pdf/src/templates/VieVinum/helpers/stands';

type Product = Vv_ProductFiltersFragment['product'] &
  Omit<Vv_ProductFiltersFragment, '__typename'>;

/** A helper to only get wines. */
function wine(product: Product) {
  if (product.__typename === 'Bottlebooks_Wine') return product;
  // @ts-ignore The typename is rewritten at the top level.
  if (product.__typename === 'Wine') return product;
  return undefined;
}

export default function getProductFilters(): FilterDefinitions<Product> {
  return {
    aggregations: {
      productType: {
        title: defineMessage({ message: 'Product types' }),
        conjunction: false,
        get: (product) =>
          wine(product)?.wineType ||
          // product.spiritType ||
          // product.beerType ||
          product.productType,
        size: 20,
        translations: {
          WINE: defineMessage({ message: 'Other wine' }),
          SPIRIT: defineMessage({ message: 'Spirit' }),
          Stillwein: defineMessage({ message: 'Wine' }),
          BEER: defineMessage({ message: 'Beer and more' }),
        },
      },

      wineColor: {
        title: defineMessage({ message: 'Wine color' }),
        conjunction: false,
        get: (product) => wine(product)?.wineColor || [],
      },

      country: {
        title: defineMessage({ message: 'Countries' }),
        conjunction: false,
        multiSelect: false,
        size: 100,
        get: (product) => product.countryName || undefined, // Excludes nulls
      },

      regionHierarchy: {
        title: defineMessage({ message: 'Winegrowing regions' }),
        conjunction: false,
        // multiSelect: false,
        size: 100,
        get: (product) => product.regionHierarchy || [], // Excludes nulls
      },

      vintage: {
        title: defineMessage({ message: 'Vintages' }),
        conjunction: false,
        size: 50,
        sort: 'term',
        order: 'desc',
        get: (product) => {
          return wine(product)?.vintage || undefined;
        }, // Excludes nulls
        translations: {
          NV: defineMessage({ message: 'NV' }),
        },
      },

      priceRanges: {
        title: defineMessage({ message: 'Price ranges' }),
        conjunction: false,
        sort: 'term',
        // This comes from ProductsTemplate.afterQuery.
        get: (product) => product.pricing?.priceRange || undefined, // Excludes nulls
        translations: {
          '€': defineMessage({ message: 'Inexpensive (-5 EUR)' }),
          '€€': defineMessage({ message: 'Mid-priced (5-10 EUR)' }),
          '€€€': defineMessage({ message: 'High-priced (10-20 EUR)' }),
          '€€€€': defineMessage({ message: 'Premium (20-50 EUR)' }),
          '€€€€€': defineMessage({ message: 'Super premium (50-100 EUR)' }),
          '€€€€€€': defineMessage({ message: 'Luxury (100+ EUR)' }),
        },
      },

      primaryGrapeVariety: {
        title: defineMessage({ message: 'Primary grape varieties' }),
        conjunction: false,
        size: 300,
        get: (product) => {
          const varieties = wine(product)
            ?.grapeVarieties?.map(({ varietyName }) => varietyName)
            .filter(Boolean);
          if (!varieties?.length) return [];
          return varieties[0];
        },
      },

      allGrapeVarieties: {
        title: defineMessage({ message: 'All grape varieties' }),
        conjunction: true,
        multiSelect: true,
        size: 300,
        get: (product) => {
          const varieties = wine(product)
            ?.grapeVarieties?.map(({ varietyName }) => varietyName)
            .filter(Boolean);
          if (!varieties?.length) return [];
          if (varieties.length === 1) return ['Single variety', ...varieties];
          if (varieties.length > 1) return ['Blend', ...varieties];
          return varieties;
        },
        translations: {
          Blend: defineMessage({ message: 'Blend' }),
          'Single variety': defineMessage({ message: 'Single variety' }),
        },
      },

      // originLevel: {
      //   title: defineMessage({ message: 'Origin level' }),
      //   conjunction: false,
      //   get: (product) =>
      //     (product.engereHerkunft?.__typename ===
      //       'Bottlebooks_SelectFieldValue' &&
      //       product.engereHerkunft?.value) ||
      //     [],
      // },

      vineyard: {
        title: defineMessage({ message: 'Vineyards' }),
        conjunction: false,
        size: 800,
        get: (product) => {
          const vineyard = wine(product)?.vineyard;
          if (!vineyard) return [];
          if (vineyard === '__null') return [];
          return vineyard.trim();
        },
      },

      productionMethods: {
        title: defineMessage({ message: 'Production methods' }),
        conjunction: false,
        size: 20,
        get: (product) =>
          wine(product)?.characteristics?.filter((value) =>
            [
              'VEGAN',
              'ORGANIC',
              'VEGETARIAN',
              'BIODYNAMIC',
              'KOSHER',
              'SUSTAINABLE',
            ].includes(value)
          ),
        translations: {
          VEGAN: defineMessage({ message: 'vegan' }),
          ORGANIC: defineMessage({ message: 'organic' }),
          VEGETARIAN: defineMessage({ message: 'vegetarian' }),
          BIODYNAMIC: defineMessage({ message: 'biodynamic' }),
          KOSHER: defineMessage({ message: 'kosher' }),
          SUSTAINABLE: defineMessage({ message: 'sustainable' }),
        },
      },

      classification: {
        title: defineMessage({ message: 'Classifications' }),
        conjunction: false,
        size: 50,
        get: (product) => {
          const classification = wine(product)?.classification;
          if (!classification) return undefined; // Excludes nulls
          if (classification === '----') return undefined;
          if (classification === 'No classification') return undefined;
          return classification;
        },
      },

      specialClassification: {
        title: defineMessage({ message: 'Special classifications' }),
        conjunction: false,
        size: 50,
        get: (product) => {
          const specialClassification = wine(product)?.specialClassification;
          if (!specialClassification) return undefined; // Excludes nulls
          if (specialClassification === '----') return undefined;
          return specialClassification;
        },
      },

      designation: {
        title: defineMessage({ message: 'Designations' }),
        conjunction: false,
        size: 50,
        get: (product) => wine(product)?.designation || undefined, // Excludes nulls
      },

      awards: {
        title: defineMessage({ message: 'Awards' }),
        conjunction: false,
        size: 50,
        get: (product) => {
          const exhibitorPackage =
            product?.registration?.exhibitorPackage?.__typename ===
              'Bottlebooks_SelectFieldValue' &&
            product?.registration?.exhibitorPackage?.key;
          if (exhibitorPackage !== 'premium' && exhibitorPackage !== 'pavilion')
            return [];
          const awards =
            product?.product?.awards?.map((award) => award.award) || [];
          const publications =
            product?.product?.tastingNotes?.map(
              (review) => review.publication
            ) || [];

          return [...awards, ...publications].filter(Boolean);
        },
      },

      // closureTypes: {
      //   title: defineMessage({ message: 'Closure types' }),
      //   conjunction: false,
      //   size: 50,
      //   get: (product) => product.bottleVariants?.[0]?.closureType || undefined, // Excludes nulls
      // },
    },

    sortings: {
      // Sort by producer name, but also group by registration.
      byProducerName: {
        title: defineMessage({ message: 'Producer name' }),
        field: [
          (product) => product?.producer?.sortName || product?.producer?.name,
          (product) => stands.getNumber(product?.registration?.stand?.name),
        ],
        order: 'asc',
        group: (product) =>
          `${product?.registration?.registrationId}::${product?.producer?.producerId}`,
        groupHeader: 'producer',
      },
      byProductName: {
        title: defineMessage({ message: 'Product name' }),
        field: [sortBy.hasImage, sortBy.name, 'vintage'],
        order: ['asc', 'asc', 'desc'],
      },
      // byExhibitorName: {
      //   title: defineMessage({ message: 'Exhibitor name' }),
      //   field: [
      //     'exhibitor.sortName',
      //     'exhibitor.name',
      //     'producer.sortName',
      //     'producer.name',
      //   ],
      //   order: 'asc',
      //   group: 'exhibitorId',
      // },
      // byStandNumber: {
      //   title: defineMessage({ message: 'Table number' }),
      //   field: [
      //     sortBy.standName,
      //     'exhibitor.sortName',
      //     'exhibitor.name',
      //     'producer.sortName',
      //     'producer.name',
      //   ],
      //   order: 'asc',
      //   group: 'registration.registrationId',
      // },
    },
  };
}

// Sort helper functions
const sortBy = {
  hasImage: (product: Product) => {
    // @ts-ignore We don't need to load a bottle image URL - we just need to know if it exists.
    if (product.bottleImage) return 0;
    return 1;
  },
  name: (product: Product) => {
    const normalized = {
      ...product,
      __typename: product.__typename?.replace('Bottlebooks_', ''),
    };
    // @ts-ignore
    const prefix = ProductHelpers.originPrefix(normalized);
    // @ts-ignore
    const name = ProductHelpers.nameWithoutOrigin(normalized);
    // @ts-ignore
    const suffix = ProductHelpers.originSuffix(normalized);
    // console.log(`${prefix} ${name} ${suffix}`.trim());
    return `${prefix} ${name} ${suffix}`.trim();
  },

  standName: (product: Product) => {
    return product?.registration?.stand?.name
      .split(/[\s,-]+/)
      .map((segment) =>
        segment === 'Tisch'
          ? undefined
          : Number(segment)
          ? segment.padStart(3, '0')
          : segment
      )
      .filter(Boolean);
  },
};

export const fragment = graphql`
  fragment vv_productFilters on Bottlebooks_RegisteredProduct {
    registration {
      registrationId
      exhibitorPackage: customFieldValue(key: "exhibitorPackage") {
        ... on Bottlebooks_SelectFieldValue {
          key
        }
      }
      profile {
        sortName
        name
      }
      stand {
        name
      }
    }
    pricing {
      priceRange
    }
    # engereHerkunft: customFieldValue(key: "engereHerkunft") {
    #   __typename
    #   ... on Bottlebooks_SelectFieldValue {
    #     value
    #   }
    # }
    product {
      __typename
      name
      shortName

      countryCode: country(format: RAW)
      countryName: country(format: LOCALIZED)
      regionHierarchy
      producer {
        producerId
        sortName
        name
        # For rendering the producer header
        countryName: country(format: LOCALIZED)
        countryCode: country(format: RAW)
        city
      }
      productType
      ... on Bottlebooks_Wine {
        wineType
        wineColor
        characteristics
        classification
        specialClassification
        designation
        vintage(removeNonVintage: true)
        denomination
        vineyard
        grapeVarieties {
          varietyName
        }
      }
      awards {
        award
      }
      tastingNotes {
        publication
      }
      # ... on Bottlebooks_Beer {
      #   beerType
      # }
      ... on Bottlebooks_Spirit {
        spiritType
      }
    }
  }
`;
